// Power dialer session logic. Pure and import-free so jest can load it
// (jest can't resolve the webpack aliases). The store owns side effects.

export const STATES = {
  START: "start", // checks shown, nothing dialed yet
  DIALING: "dialing", // ringing
  CONNECTED: "connected", // live with a person
  VOICEMAIL: "voicemail", // machine answered, waiting for Drop (V) / Skip (S)
  WRAPUP: "wrapup", // connected call ended, rep picks an outcome
  AUTOLOG: "autolog", // connected call ended, AI confident, logging in 5s
  COUNTDOWN: "countdown", // between calls
  PAUSED: "paused", // rep pressed Wait
  DONE: "done"
};

export const OUTCOMES = {
  CONNECTED: "Connected",
  NO_ANSWER: "No answer",
  BUSY: "Busy",
  WRONG_NUMBER: "Wrong number",
  LEFT_VOICEMAIL: "Left voicemail",
  VOICEMAIL_NONE_LEFT: "Voicemail, none left"
};

export const DEFAULT_SETTINGS = {
  detectVoicemail: true,
  autoVoicemailDrop: false,
  includeMobileInAutoDrop: false,
  ringSeconds: 25,
  countdownSeconds: 3, // 0 = off
  afterConnected: "wait", // "wait" | "autolog"
  autoLogMapping: {
    "no-answer": OUTCOMES.NO_ANSWER,
    busy: OUTCOMES.BUSY,
    failed: OUTCOMES.WRONG_NUMBER
  },
  markNumberBad: true,
  numberOrder: ["phone", "mobilePhone", "otherPhone"],
  tryNextNumberOnNoAnswer: false,
  callingHours: { from: "08:00", to: "20:00" },
  skipCalledWithinHours: 24,
  skipRepliedOrOptedOut: true,
  redialNoAnswersAtEnd: false,
  noPhoneNumber: "skip" // "ask" | "find" | "skip"
};

export const AUTO_LOG_CONFIDENCE = 0.9;
export const AUTO_LOG_SECONDS = 5;

const MACHINE = /^machine_/;

// Call-state poll ({status, answeredBy, dialResult}) -> what happened, or null while ringing.
export const resultKind = ({ status, answeredBy, dialResult } = {}) => {
  if (MACHINE.test(answeredBy || "")) return "voicemail";
  if (["no-answer", "busy", "failed"].includes(dialResult)) return dialResult;
  if (dialResult === "canceled") return "no-answer";
  if (status === "in-progress" || answeredBy === "human") return "connected";
  if (status === "completed") return dialResult === "completed" ? "connected" : "no-answer";
  return null;
};

export const isMobile = item => item?.phoneType === "mobilePhone";

export const shouldAutoDrop = (settings = DEFAULT_SETTINGS, item = {}) =>
  !!settings.autoVoicemailDrop &&
  (!isMobile(item) || !!settings.includeMobileInAutoDrop);

export const canAutoLog = (settings = DEFAULT_SETTINGS, confidence = 0) =>
  settings.afterConnected === "autolog" && confidence >= AUTO_LOG_CONFIDENCE;

const entry = (item, outcome, extra = {}) => ({
  taskId: item?.taskId,
  contactId: item?.contactId,
  name: item?.name,
  outcome,
  note: "",
  autoLogged: false,
  ...extra
});

/*
 * One step of the session. Returns { state, log } where log is the call-log
 * entry to write (or null). ctx = { settings, item, kind, confidence, outcome,
 * notes, suggestion }.
 */
export const transition = (state, event, ctx = {}) => {
  const { settings = DEFAULT_SETTINGS, item = {} } = ctx;
  const next = s => ({ state: s, log: null });
  const logged = log => ({ state: STATES.COUNTDOWN, log });

  if (event === "END") return next(STATES.DONE);

  switch (state) {
    case STATES.START:
    case STATES.PAUSED:
      return event === "DIAL" ? next(STATES.DIALING) : next(state);

    case STATES.COUNTDOWN:
      if (event === "DIAL") return next(STATES.DIALING);
      if (event === "WAIT") return next(STATES.PAUSED);
      if (event === "EMPTY") return next(STATES.DONE);
      return next(state);

    case STATES.DIALING: {
      if (event !== "RESULT") return next(state);
      const { kind } = ctx;
      if (kind === "connected") return next(STATES.CONNECTED);
      if (kind === "voicemail") {
        if (!settings.detectVoicemail) return next(STATES.CONNECTED);
        return shouldAutoDrop(settings, item)
          ? logged(
            entry(item, OUTCOMES.LEFT_VOICEMAIL, {
              note: "Auto voicemail dropped",
              autoLogged: true,
              voicemailDropped: true
            })
          )
          : next(STATES.VOICEMAIL);
      }
      if (kind === "no-answer" && settings.tryNextNumberOnNoAnswer) {
        const retry = nextNumber(item);
        if (retry) return { state: STATES.COUNTDOWN, log: null, retry };
      }
      const mapping = { ...DEFAULT_SETTINGS.autoLogMapping, ...settings.autoLogMapping };
      return mapping[kind]
        ? logged(entry(item, mapping[kind], { autoLogged: true, result: kind }))
        : next(state);
    }

    case STATES.VOICEMAIL:
      if (event === "DROP")
        return logged(
          entry(item, OUTCOMES.LEFT_VOICEMAIL, {
            note: "Voicemail dropped",
            voicemailDropped: true
          })
        );
      if (event === "SKIP") return logged(entry(item, OUTCOMES.VOICEMAIL_NONE_LEFT));
      return next(state);

    case STATES.CONNECTED:
      if (event === "RESULT" && ctx.kind === "voicemail") return transition(STATES.DIALING, event, ctx);
      if (event !== "HANGUP") return next(state);
      return next(canAutoLog(settings, ctx.confidence) ? STATES.AUTOLOG : STATES.WRAPUP);

    case STATES.AUTOLOG:
      if (event === "CHANGE") return next(STATES.WRAPUP);
      if (event === "LOG" || event === "TIMEOUT")
        return logged(
          entry(item, ctx.outcome || OUTCOMES.CONNECTED, {
            note: [ctx.suggestion, ctx.notes].filter(Boolean).join("\n"),
            autoLogged: true,
            connected: true,
            confidence: ctx.confidence
          })
        );
      return next(state);

    case STATES.WRAPUP:
      return event === "LOG"
        ? logged(
          entry(item, ctx.outcome || OUTCOMES.CONNECTED, {
            note: ctx.notes || "",
            connected: true,
            confidence: ctx.confidence
          })
        )
        : next(state);

    default:
      return next(state);
  }
};

// Next dialable queue index after `from` (-1 = from the start), or -1 if none.
// Skips removed / no-number / already-dialed items and contacts already dialed this session.
// Redial copies (pass 2) only clash with other pass-2 dials.
export const nextIndex = (queue = [], from = -1) => {
  const key = q => `${q.contactId}:${q.pass || 1}`;
  const dialedContacts = new Set(queue.filter(q => q.status === "dialed").map(key));
  for (let i = from + 1; i < queue.length; i++) {
    const q = queue[i];
    if (q.status === "ready" && q.phone && !dialedContacts.has(key(q))) return i;
    if (q.status === "finding") return i; // caller waits for the lookup
  }
  return -1;
};

// Contact phone fields are arrays from the API (a string is accepted too).
const firstNumber = v => (Array.isArray(v) ? v.find(Boolean) : v) || "";

export const pickPhone = (contact = {}, order = DEFAULT_SETTINGS.numberOrder) => {
  const field = order.find(f => firstNumber(contact?.[f]));
  return field ? { phone: firstNumber(contact[field]), phoneType: field } : { phone: "", phoneType: "" };
};

// Every number on the contact in setting order, deduped.
export const phoneNumbers = (contact = {}, order = DEFAULT_SETTINGS.numberOrder) => {
  const seen = new Set();
  return order.flatMap(phoneType =>
    [].concat(contact?.[phoneType] || []).filter(phone => phone && !seen.has(phone) && seen.add(phone)).map(phone => ({ phone, phoneType }))
  );
};

// Last 10 digits (whole number when shorter), so "+14155550100" matches "4155550100".
const numberKey = n => String(n).replace(/\D/g, "").slice(-10);

// phoneNumbers minus the contact's invalidPhoneNumbers.
export const validNumbers = (contact = {}, order = DEFAULT_SETTINGS.numberOrder) => {
  const invalid = new Set((contact?.invalidPhoneNumbers || []).map(numberKey));
  return phoneNumbers(contact, order).filter(n => !invalid.has(numberKey(n.phone)));
};

// The item's next untried number (tried = numbers already dialed for it), or null.
export const nextNumber = (item = {}) => {
  const tried = [item.phone, ...(item.tried || [])];
  return (item.numbers || []).find(n => !tried.includes(n.phone)) || null;
};

export const shouldMarkBad = (settings = DEFAULT_SETTINGS, log) =>
  !!settings.markNumberBad && !!log?.result && log.outcome === OUTCOMES.WRONG_NUMBER;

// Time rules apply only before a contact's first dial this session (not its next number or second pass).
export const dialSkipReason = (item = {}, settings = DEFAULT_SETTINGS, now = Date.now()) =>
  item.pass || item.tried?.length ? "" : timeSkipReason(item, settings, now);

// Session ended while waiting to try the next number: the number already tried is logged as No answer.
export const pendingRetryLog = item =>
  item?.status === "ready" && item.tried?.length
    ? entry(item, OUTCOMES.NO_ANSWER, {
      autoLogged: true,
      result: "no-answer",
      phone: item.tried[item.tried.length - 1],
      callSid: item.triedCallSid || ""
    })
    : null;

// Contact's wall clock { hhmm: "13:05", label: "1:05 PM" } from an IANA zone, or null when unknown.
export const contactClock = (timeZone, now = Date.now()) => {
  if (!timeZone) return null;
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(now);
    const get = t => parts.find(p => p.type === t)?.value || "00";
    const label = new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", minute: "2-digit" }).format(now);
    return { hhmm: `${get("hour")}:${get("minute")}`, label };
  } catch (e) {
    return null;
  }
};

export const withinHours = (hhmm, { from = "00:00", to = "24:00" } = {}) =>
  from <= to ? hhmm >= from && hhmm < to : hhmm >= from || hhmm < to;

// Skip reasons that depend on the clock; checked at queue build and again just before each dial.
export const timeSkipReason = (c = {}, settings = DEFAULT_SETTINGS, now = Date.now()) => {
  const hours = +settings.skipCalledWithinHours || 0;
  if (hours && +c.lastCalledAt && now - c.lastCalledAt < hours * 3600000) return `Called in last ${hours}h`;
  const clock = contactClock(c.timeZone, now);
  if (clock && settings.callingHours && !withinHours(clock.hhmm, settings.callingHours)) return "Outside calling hours";
  return "";
};

// Held (not yet completed) first-pass no-answers, re-queued once for the second pass on their first number.
export const redialItems = (queue = [], log = []) => {
  const noAnswer = new Set(log.filter(l => l.held && l.outcome === OUTCOMES.NO_ANSWER).map(l => l.taskId));
  return queue
    .filter(q => !q.pass && noAnswer.has(q.taskId))
    .map(q => ({ ...q, ...(q.numbers?.[0] || {}), tried: [], pass: 2, status: "ready" }));
};

/*
 * Pre-dial skip reason from data the task APIs already return, or "".
 * Stage names are team-configured, so opted out / replied match by name.
 */
export const skipReason = (row = {}, settings = DEFAULT_SETTINGS, now = Date.now()) => {
  // sequenceStatus is only set when the sequence is PAUSED / DELETED / SUSPENDED / DRAFT.
  if (row.sequenceStatus) return "sequence not active";
  const c = row.contact || {};
  const stage = `${c.stage || ""} ${c.stageDisplayName || ""}`;
  if (settings.skipRepliedOrOptedOut && (c.optedOutDate || /opt(ed)?[\s_-]*out/i.test(stage))) return "opted out";
  if (settings.skipRepliedOrOptedOut && /repl/i.test(stage)) return "replied";
  return timeSkipReason(c, settings, now);
};

/*
 * Rows from /cm/task/getTask or /cm/task/filter ({task, contact}) -> queue.
 * Call tasks only, one per contact (earliest due), input order kept.
 */
export const buildQueue = (rows = [], settings = DEFAULT_SETTINGS, now = Date.now()) => {
  const byContact = new Map();
  rows.forEach((row, order) => {
    const task = row?.task || {};
    const type = task?.touchPoint?.type || task?.taskType;
    if (type !== "phone") return;
    const contact = row?.contact || {};
    const contactId = contact.id || task.contactId || task.id;
    const due = new Date(task.taskDue).getTime() || 0;
    const prev = byContact.get(contactId);
    if (!prev || due < prev.due) byContact.set(contactId, { row, due, order: prev ? prev.order : order });
  });
  return [...byContact.values()]
    .sort((a, b) => a.order - b.order)
    .map(({ row }) => {
      const contact = row.contact || {};
      const numbers = validNumbers(contact, settings.numberOrder);
      const { phone = "", phoneType = "" } = numbers[0] || {};
      const name = [contact.firstName, contact.lastName].filter(Boolean).join(" ") || contact.email || "";
      const reason = skipReason(row, settings, now);
      return {
        taskId: row.task.id,
        contactId: contact.id || row.task.contactId,
        name,
        title: contact.jobTitle || "",
        company: contact.company || "",
        phone,
        phoneType,
        numbers,
        tried: [],
        invalidPhoneNumbers: contact.invalidPhoneNumbers || [],
        lastCalledAt: contact.lastCalledAt || 0,
        timeZone: contact.timeZone || "",
        status: reason ? "skipped" : phone ? "ready" : "no-number",
        reason
      };
    });
};

// First-pass No answer isn't completed yet when the redial pass may still call it (one complete per task).
export const holdForRedial = (settings = DEFAULT_SETTINGS, row = {}, pass) =>
  !!settings.redialNoAnswersAtEnd && !pass && row.outcome === OUTCOMES.NO_ANSWER;

// Held rows no later logged call replaced: completed as No answer at session end.
export const heldToFlush = (log = []) =>
  log.filter((l, i) => l.held && !log.slice(i + 1).some(x => x.taskId === l.taskId && !x.notLogged));

// Counts each task's final result once (a redialed no-answer counts as its last call).
export const sessionCounts = (all = []) => {
  const log = all.filter((l, i) => !l.taskId || !all.slice(i + 1).some(x => x.taskId === l.taskId));
  const count = outcome => log.filter(l => l.outcome === outcome).length;
  return {
    called: log.length,
    connected: log.filter(l => l.connected).length,
    voicemail: count(OUTCOMES.LEFT_VOICEMAIL) + count(OUTCOMES.VOICEMAIL_NONE_LEFT),
    noAnswer: count(OUTCOMES.NO_ANSWER),
    busy: count(OUTCOMES.BUSY),
    wrongNumber: count(OUTCOMES.WRONG_NUMBER),
    talkSeconds: all.reduce((n, l) => n + (l.talkSeconds || 0), 0)
  };
};

// Which leave warning applies: "live" during a call, "between" mid-session, "none" otherwise.
export const leaveGuard = state => {
  if (state === STATES.CONNECTED) return "live";
  if ([STATES.START, STATES.DONE].includes(state) || !state) return "none";
  return "between";
};

// Keyboard -> event for the current state. Off while typing (Enter in notes = newline).
export const keyAction = (key = "", state, inTextField = false) => {
  if (inTextField) return null;
  const k = key.length === 1 ? key.toLowerCase() : key;
  if (state === STATES.WRAPUP) {
    if (/^[1-8]$/.test(k)) return { event: "OUTCOME", index: +k - 1 };
    if (k === "Enter") return { event: "LOG" };
  }
  if (state === STATES.AUTOLOG) {
    if (k === "Enter") return { event: "LOG" };
    if (k === "c") return { event: "CHANGE" };
  }
  if (state === STATES.VOICEMAIL) {
    if (k === "v") return { event: "DROP" };
    if (k === "s") return { event: "SKIP" };
  }
  if (state === STATES.COUNTDOWN) {
    if (k === "Escape") return { event: "WAIT" };
    if (k === "Enter") return { event: "DIAL" };
  }
  if ([STATES.START, STATES.PAUSED].includes(state) && k === "Enter") return { event: "DIAL" };
  return null;
};

// Start check: ask about contacts with no number only when the setting is "ask" and there are some.
export const noPhonePrompt = (settings = DEFAULT_SETTINGS, queue = []) => {
  const noNumber = queue.filter(q => q.status === "no-number").length;
  if (settings.noPhoneNumber !== "ask" || !noNumber) return null;
  const ready = queue.filter(q => q.status === "ready").length;
  return { noNumber, ready, total: noNumber + ready };
};

// Copy for the leave popup (kind from leaveGuard). called = logged so far, total = dialable calls.
export const leaveCopy = (kind, { called = 0, total = 0, name = "" } = {}) => {
  if (kind === "live") {
    return {
      title: `You’re on a call with ${name.split(" ")[0] || "the contact"}`,
      body: "Finish the call before leaving. If you end it now, it’s logged as Connected and you can add notes later in Calls.",
      action: "End call and leave"
    };
  }
  const left = Math.max(total - called, 0);
  return {
    title: "End power dialer session?",
    body: `${called} of ${total} called and logged. The other ${left} call task${left === 1 ? " stays" : "s stay"} due in Tasks.`,
    action: "End session"
  };
};
